import { useCallback, useState } from "react";
import {
  Button,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link as MuiLink,
  IconButton,
} from "@mui/material";
import { useMutation } from "@apollo/client";
import {
  CREATE_DETOX_EVENT,
  UPDATE_DETOX_EVENT,
  SOFT_DELETE_DETOX_EVENT,
  GET_DETOX_EVENTS_FOR_SESSION,
} from "../Queries/DetoxQueries";
import { DetoxEvent } from "../model/detox";
import { setLastError } from "../store/session";
import { useAppDispatch } from "../store";
import { TableLabel } from "./tables";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { MainButton } from "./buttons";
import { mapGraphQLError } from "../utils/detox";

interface TemporaryEvent {
  name: string;
  description?: string | null;
  link?: string | null;
}

interface DialogState {
  isOpen: boolean;
  editingEvent: DetoxEvent | TemporaryEvent | null;
  editingIndex: number | null;
  formData: {
    name: string;
    description: string;
    link: string;
  };
}

interface DetoxEventManagerProps {
  sessionId?: string; // Optional for temporary events during session creation
  events: DetoxEvent[] | TemporaryEvent[];
  onEventsChange?: (events: TemporaryEvent[]) => void; // For temporary events during creation
  isTemporary?: boolean; // If true, events are temporary and don't have IDs yet
}

export const DetoxEventManager = ({
  sessionId,
  events,
  onEventsChange,
  isTemporary = false,
}: DetoxEventManagerProps) => {
  const dispatch = useAppDispatch();
  const [dialogState, setDialogState] = useState<DialogState>({
    isOpen: false,
    editingEvent: null,
    editingIndex: null,
    formData: {
      name: "",
      description: "",
      link: "",
    },
  });

  const [createEvent] = useMutation(CREATE_DETOX_EVENT, {
    refetchQueries: sessionId ? [
      {
        query: GET_DETOX_EVENTS_FOR_SESSION,
        variables: { session_id: sessionId },
      }
    ] : [],
    onCompleted: () => {
      handleCloseDialog();
    },
    onError: (error) => {
      const friendlyError = mapGraphQLError(error);
      dispatch(setLastError(friendlyError));
    },
  });

  const [updateEvent] = useMutation(UPDATE_DETOX_EVENT, {
    refetchQueries: sessionId ? [
      {
        query: GET_DETOX_EVENTS_FOR_SESSION,
        variables: { session_id: sessionId },
      }
    ] : [],
    onCompleted: () => {
      handleCloseDialog();
    },
    onError: (error) => {
      const friendlyError = mapGraphQLError(error);
      dispatch(setLastError(friendlyError));
    },
  });

  const [deleteEvent] = useMutation(SOFT_DELETE_DETOX_EVENT, {
    refetchQueries: sessionId ? [
      {
        query: GET_DETOX_EVENTS_FOR_SESSION,
        variables: { session_id: sessionId },
      }
    ] : [],
    onError: (error) => {
      const friendlyError = mapGraphQLError(error);
      dispatch(setLastError(friendlyError));
    },
  });

  const handleOpenDialog = useCallback((event?: DetoxEvent | TemporaryEvent, index?: number) => {
    if (event) {
      setDialogState({
        isOpen: true,
        editingEvent: event,
        editingIndex: index !== undefined ? index : null,
        formData: {
          name: event.name,
          description: event.description || "",
          link: event.link || "",
        },
      });
    } else {
      setDialogState({
        isOpen: true,
        editingEvent: null,
        editingIndex: null,
        formData: {
          name: "",
          description: "",
          link: "",
        },
      });
    }
  }, []);

  const handleCloseDialog = useCallback(() => {
    setDialogState({
      isOpen: false,
      editingEvent: null,
      editingIndex: null,
      formData: {
        name: "",
        description: "",
        link: "",
      },
    });
  }, []);

  const handleSave = useCallback(() => {
    const { formData, editingEvent, editingIndex } = dialogState;
    
    if (!formData.name.trim()) {
      dispatch(setLastError("Event name is required"));
      return;
    }

    const variables: any = {
      name: formData.name.trim(),
      description: formData.description.trim() || null,
      link: formData.link.trim() || null,
    };

    if (isTemporary && onEventsChange) {
      // Handle temporary events during session creation
      const tempEvents = [...events] as TemporaryEvent[];
      if (editingIndex !== null && editingIndex >= 0) {
        // Update existing temporary event
        tempEvents[editingIndex] = variables;
      } else {
        // Add new temporary event
        tempEvents.push(variables);
      }
      onEventsChange(tempEvents);
      handleCloseDialog();
    } else if (sessionId) {
      // Handle real events with sessionId
      if (editingEvent && 'id' in editingEvent) {
        updateEvent({
          variables: {
            id: editingEvent.id,
            ...variables,
          },
        });
      } else {
        createEvent({
          variables: {
            session_id: sessionId,
            ...variables,
          },
        });
      }
    }
  }, [dialogState, sessionId, createEvent, updateEvent, dispatch, isTemporary, onEventsChange, events, handleCloseDialog]);

  const handleDelete = useCallback((event: DetoxEvent | TemporaryEvent, index?: number) => {
    if (isTemporary && onEventsChange && index !== undefined) {
      // Handle temporary event deletion
      if (window.confirm(`Are you sure you want to remove "${event.name}"?`)) {
        const tempEvents = [...events] as TemporaryEvent[];
        tempEvents.splice(index, 1);
        onEventsChange(tempEvents);
      }
    } else if ('id' in event) {
      // Handle real event deletion
      if (window.confirm(`Are you sure you want to delete "${event.name}"?`)) {
        deleteEvent({
          variables: {
            id: event.id,
          },
        });
      }
    }
  }, [deleteEvent, isTemporary, onEventsChange, events]);

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle2">Manage Events</Typography>
        <MainButton
          size="small"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Event
        </MainButton>
      </Stack>

      {events.length === 0 ? (
        <Paper>
          <Stack padding={2} alignItems="center">
            <Typography color="text.secondary">No events yet. Add one to get started.</Typography>
          </Stack>
        </Paper>
      ) : (
        <TableContainer 
          component={Paper}
          sx={{ maxHeight: '60vh', overflow: 'auto' }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableLabel label="Name" />
                <TableLabel label="Description" />
                <TableLabel label="Link" />
                <TableLabel label="Actions" />
              </TableRow>
            </TableHead>
            <TableBody>
              {events.map((event, index) => {
                const eventId = 'id' in event ? event.id : `temp-${index}`;
                return (
                  <TableRow key={eventId}>
                    <TableCell sx={{ maxWidth: 200, wordBreak: 'break-word', verticalAlign: 'top' }}>
                      <Typography fontWeight="medium">{event.name}</Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 300, wordBreak: 'break-word', verticalAlign: 'top' }}>
                      <Typography variant="body2" color="text.secondary">
                        {event.description || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 300, wordBreak: 'break-word', verticalAlign: 'top' }}>
                      {event.link ? (
                        <MuiLink href={event.link} target="_blank" rel="noopener" sx={{ wordBreak: 'break-all' }}>
                          {event.link}
                        </MuiLink>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top' }}>
                      <Stack direction="row" spacing={1}>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleOpenDialog(event, index)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(event, index)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogState.isOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogState.editingEvent ? "Edit Event" : "Add Event"}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} paddingTop={1}>
            <TextField
              fullWidth
              label="Name"
              value={dialogState.formData.name}
              onChange={(e) => setDialogState(prev => ({
                ...prev,
                formData: { ...prev.formData, name: e.target.value }
              }))}
              required
              autoFocus
            />
            <TextField
              fullWidth
              label="Description"
              value={dialogState.formData.description}
              onChange={(e) => setDialogState(prev => ({
                ...prev,
                formData: { ...prev.formData, description: e.target.value }
              }))}
              multiline
              rows={3}
            />
            <TextField
              fullWidth
              label="Link"
              value={dialogState.formData.link}
              onChange={(e) => setDialogState(prev => ({
                ...prev,
                formData: { ...prev.formData, link: e.target.value }
              }))}
              placeholder="https://..."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!dialogState.formData.name.trim()}
          >
            {dialogState.editingEvent ? "Save" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

